/**
 * "Has the enrolled biometric set changed since we last trusted it?" — asked
 * silently, with no prompt and no authentication.
 *
 * Native implementation:
 * `apps/frontend/android/app/src/main/java/ai/panluma/app/BiometrySetGuardPlugin.java`,
 * which explains at length why this is a canary key rather than an access
 * control on the token's own key. In short: on Android only a per-use,
 * biometric-only key is invalidated by enrolment, and putting that on the token
 * would cost a second prompt at every unlock and remove the device-passcode
 * fallback. The canary buys the same observable behaviour — a new fingerprint
 * forces a full PanLuma login — for neither.
 *
 * iOS has no implementation yet. `arm()` therefore reports `unavailable` there,
 * the token is stored unguarded, and nothing changes from today's behaviour. The
 * iOS half is `LAContext.evaluatedPolicyDomainState`, which answers the same
 * question equally silently; see `docs/platform/mobile-app.md` § Biometry-bound
 * refresh token.
 *
 * A **leaf**: it may import the platform flag and nothing else, because
 * `secureTokenStore` — itself a leaf under `axiosInstance` and `AuthContext` —
 * depends on it.
 */
import { registerPlugin } from '@capacitor/core';

import { isCapacitor } from '../api/config';

/** Whether the canary could be armed against the set enrolled right now. */
export type GuardArmResult = 'armed' | 'unavailable';

/**
 * What the canary says.
 *
 * - `unchanged` — the same biometrics are enrolled as when it was armed.
 * - `changed` — somebody added or removed one. The session on this device is
 *   over and the person completes a full login.
 * - `unavailable` — no answer right now: nothing armed, no plugin, a keystore
 *   that is busy. Never a reason to end a session.
 */
export type GuardCheckResult = 'unchanged' | 'changed' | 'unavailable';

interface BiometrySetGuardPlugin {
  arm: () => Promise<{ state: string; code: string }>;
  check: () => Promise<{ state: string; code: string }>;
  disarm: () => Promise<void>;
}

/**
 * Registered on FIRST USE, not at import. Registering at module scope makes the
 * web bundle claim a native-only plugin name the moment anything in the auth
 * chain is imported, and warns on every re-import.
 */
let registered: BiometrySetGuardPlugin | null = null;

/** `null` restores the real plugin. Not used by application code. */
let overrideForTests: BiometrySetGuardPlugin | null = null;

/**
 * How long a canary call may stay in flight before it is read as `unavailable`.
 *
 * `arm()` sits inside `setRefreshToken`, ahead of the queue and therefore ahead
 * of the login it is part of, so a native half that accepts a call and never
 * calls back is a login that never completes — the same liveness hole
 * `KEYSTORE_OP_TIMEOUT_MS` closes one layer down, and this module is the only
 * other native leg on that path. Both answers this module can give are already
 * safe by construction (`unavailable` never ends a session), so a deadline
 * costs nothing but the wait.
 */
const GUARD_CALL_TIMEOUT_MS = 5000;

/**
 * The state a timed-out call reports, distinct from anything the native half can
 * return so `arm()` can tell "did not answer" from "answered no".
 */
const TIMED_OUT = 'panluma-guard-timeout';

/** Resolve to `fallback` if `call` outlives the deadline. */
async function withGuardDeadline<T>(call: Promise<T>, fallback: T): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  const deadline = new Promise<T>((resolve) => {
    timer = setTimeout(() => resolve(fallback), GUARD_CALL_TIMEOUT_MS);
  });
  try {
    return await Promise.race([call, deadline]);
  } finally {
    clearTimeout(timer!);
  }
}

export function __setBiometrySetGuardForTests(
  plugin: BiometrySetGuardPlugin | null
): void {
  overrideForTests = plugin;
}

function plugin(): BiometrySetGuardPlugin {
  if (overrideForTests) return overrideForTests;
  registered ??= registerPlugin<BiometrySetGuardPlugin>('BiometrySetGuard');
  return registered;
}

/**
 * Sticky for the process once arming has failed.
 *
 * The two reasons it fails — no biometry enrolled, no native half in this binary
 * — do not change while the app is running, and without this memo the policy
 * sync would re-store the refresh token on every refetch, each time asking for a
 * guard it has already been told it cannot have.
 *
 * A native half that took the call and never ANSWERED is deliberately NOT
 * remembered. The two reasons above are facts about the binary and the device's
 * enrolment; a timeout is a fact about one moment, and remembering it would
 * silently drop the app back to pre-guard behaviour — a token stored unbound,
 * readable without a live authentication — for the rest of the session, over a
 * single slow call. The cost of re-asking is bounded by the same deadline and
 * paid only while the guard is actually wedged; the cost of remembering is the
 * security property this module exists for. So `arm()` reports `unavailable`
 * either way and only a REPORTED failure is sticky.
 */
let knownUnavailable = false;

export function isGuardKnownUnavailable(): boolean {
  return knownUnavailable;
}

/** Not used by application code. */
export function __resetBiometrySetGuardForTests(): void {
  overrideForTests = null;
  knownUnavailable = false;
}

/**
 * Start watching the currently enrolled set. Call this whenever we newly decide
 * to trust it — a full login, or the app lock being switched on.
 *
 * Never throws. A device with no biometry enrolled, or a build without the
 * native half, reports `unavailable`, and the caller stores the token exactly as
 * it did before this existed.
 */
export async function armBiometrySetGuard(): Promise<GuardArmResult> {
  if (!isCapacitor) return 'unavailable';
  try {
    const { state } = await withGuardDeadline(plugin().arm(), { state: TIMED_OUT, code: '' });
    if (state === 'armed') return 'armed';
    // A call that never answered says nothing about whether this device HAS a
    // guard, so it does not get to retire the feature for the process.
    if (state === TIMED_OUT) return 'unavailable';
  } catch {
    // Treated exactly like a reported failure: there is no third answer a
    // caller could act on.
  }
  knownUnavailable = true;
  return 'unavailable';
}

/**
 * Ask the canary. Silent — no prompt, no authentication, ~1ms.
 *
 * An unrecognised answer is `unavailable`, not `changed`: the cost of the two
 * mistakes is not symmetrical. Reading a busy keystore as a changed set signs
 * somebody out of their offline data; reading a changed set as busy leaves them
 * locked, asking again on the next attempt.
 */
export async function checkBiometrySetGuard(): Promise<GuardCheckResult> {
  if (!isCapacitor) return 'unavailable';
  try {
    const { state } = await withGuardDeadline(plugin().check(), { state: TIMED_OUT, code: '' });
    if (state === 'unchanged') return 'unchanged';
    if (state === 'changed') return 'changed';
    return 'unavailable';
  } catch {
    return 'unavailable';
  }
}

/** Stop watching. Sign-out only. Never throws. */
export async function disarmBiometrySetGuard(): Promise<void> {
  if (!isCapacitor) return;
  try {
    await plugin().disarm();
  } catch {
    // A stale canary costs the next sign-in one extra arm() and nothing else.
  }
}
